// Heuristic-driven Tetris AI based on the El-Tetris / Pierre Dellacherie
// weighting (aggregate height, lines cleared, holes, bumpiness).
//
// The AI exposes a "next-action" stream paced by a target Pieces Per Second.
// It works on a snapshot of any Engine — same engine class as the player —
// so the player's view of the AI's board is rendered identically.

import { BOARD_BUFFER_HEIGHT, BOARD_VISIBLE_HEIGHT, BOARD_WIDTH } from '../config';
import type { Engine } from '../game/engine';
import { kicks, shapeOf } from '../game/pieces';
import type { ActivePiece, Cell, PieceType, Rotation } from '../types';

export interface BotDifficulty {
  id: 'easy' | 'medium' | 'hard' | 'nightmare';
  name: string;
  targetPps: number;        // pieces per second the bot tries to maintain
  mistakeChance: number;    // 0..1 probability of picking a slightly worse move
  weights: HeuristicWeights;
  /** Garbage held back: when the bot would top out from incoming garbage, it can swap a held tetris setup. */
}

export interface HeuristicWeights {
  aggregateHeight: number;
  lines: number;
  holes: number;
  bumpiness: number;
  wells: number;
  /** Prefer keeping right column open so it can stack I-piece tetrises. */
  rightWell: number;
}

export const DIFFICULTIES: Record<BotDifficulty['id'], BotDifficulty> = {
  easy: {
    id: 'easy', name: 'easy', targetPps: 0.6, mistakeChance: 0.25,
    weights: { aggregateHeight: -0.32, lines: 0.50, holes: -0.55, bumpiness: -0.08, wells: -0.06, rightWell: 0.0 },
  },
  medium: {
    id: 'medium', name: 'medium', targetPps: 1.3, mistakeChance: 0.08,
    weights: { aggregateHeight: -0.51, lines: 0.76, holes: -0.36, bumpiness: -0.18, wells: -0.08, rightWell: 0.15 },
  },
  hard: {
    id: 'hard', name: 'hard', targetPps: 2.2, mistakeChance: 0.02,
    weights: { aggregateHeight: -0.51, lines: 0.76, holes: -0.36, bumpiness: -0.18, wells: -0.08, rightWell: 0.30 },
  },
  nightmare: {
    id: 'nightmare', name: 'nightmare', targetPps: 3.6, mistakeChance: 0,
    weights: { aggregateHeight: -0.51, lines: 0.76, holes: -0.36, bumpiness: -0.18, wells: -0.08, rightWell: 0.40 },
  },
};

interface PlacementPlan {
  rotation: Rotation;
  x: number;          // target anchor x
  score: number;
  hardDrop: boolean;
}

interface PendingAction {
  kind: 'rotate-cw' | 'rotate-ccw' | 'shift-left' | 'shift-right' | 'hard-drop' | 'hold';
}

export class Bot {
  engine: Engine;
  difficulty: BotDifficulty;
  private plan: PlacementPlan | null = null;
  private actions: PendingAction[] = [];
  private nextActionAtMs = 0;
  private elapsedMs = 0;
  private lastPieceSeq = -1;

  constructor(engine: Engine, difficulty: BotDifficulty) {
    this.engine = engine;
    this.difficulty = difficulty;
  }

  /** Tick — called every game tick. Returns one action to execute (or null). */
  tick(dtMs: number): PendingAction | null {
    if (this.engine.state !== 'playing' || !this.engine.piece) return null;
    this.elapsedMs += dtMs;
    // Recompute plan whenever a new piece spawns.
    if (this.engine.pieceSeq !== this.lastPieceSeq) {
      this.lastPieceSeq = this.engine.pieceSeq;
      this.plan = this.pickPlan(this.engine.piece);
      if (this.plan) this.actions = this.actionsForPlan(this.engine.piece, this.plan);
    }
    if (this.elapsedMs < this.nextActionAtMs) return null;
    // Pace actions so the bot's PPS matches difficulty
    const actionsPerPiece = Math.max(4, this.actions.length + 1); // approximate
    const msPerAction = (1000 / this.difficulty.targetPps) / actionsPerPiece;
    this.nextActionAtMs = this.elapsedMs + msPerAction;
    const next = this.actions.shift();
    return next ?? { kind: 'hard-drop' };
  }

  setDifficulty(d: BotDifficulty): void {
    this.difficulty = d;
    this.lastPieceSeq = -1;
    this.plan = null;
    this.actions = [];
  }

  // ---- planning ----
  private pickPlan(piece: ActivePiece): PlacementPlan {
    const baseGrid = this.snapshotGrid();
    let best: PlacementPlan | null = null;
    let runnerUp: PlacementPlan | null = null;
    for (let rot = 0 as Rotation; rot < 4; rot = (rot + 1) as Rotation) {
      for (let x = -2; x < BOARD_WIDTH + 2; x++) {
        const test: ActivePiece = { type: piece.type, rotation: rot, x, y: 0 };
        // Skip if piece is out of bounds at every y
        if (this.outOfBounds(test)) continue;
        const dropped = this.dropPiece(baseGrid, test);
        if (!dropped) continue;
        const cellsAdded = this.applyPlace(baseGrid, dropped);
        const cleared = this.countAndClearFull(baseGrid);
        const score = this.evaluate(baseGrid, cleared);
        this.undoPlace(baseGrid, cellsAdded, cleared);
        const plan: PlacementPlan = { rotation: rot, x: dropped.x, score, hardDrop: true };
        if (!best || score > best.score) {
          runnerUp = best;
          best = plan;
        } else if (!runnerUp || score > runnerUp.score) {
          runnerUp = plan;
        }
      }
      if (piece.type === 'O') break;
    }
    if (!best) return { rotation: 0, x: 3, score: 0, hardDrop: true };
    // Occasionally pick the runner-up to simulate human-level mistakes.
    if (runnerUp && Math.random() < this.difficulty.mistakeChance) return runnerUp;
    return best;
  }

  private actionsForPlan(piece: ActivePiece, plan: PlacementPlan): PendingAction[] {
    const out: PendingAction[] = [];
    // Rotation steps
    let r = piece.rotation;
    while (r !== plan.rotation) {
      // Prefer the shorter direction
      const cwSteps = ((plan.rotation - r + 4) % 4);
      if (cwSteps <= 2) { out.push({ kind: 'rotate-cw' }); r = ((r + 1) & 3) as Rotation; }
      else { out.push({ kind: 'rotate-ccw' }); r = ((r + 3) & 3) as Rotation; }
    }
    // Horizontal shifts
    const dx = plan.x - piece.x;
    for (let i = 0; i < Math.abs(dx); i++) out.push({ kind: dx < 0 ? 'shift-left' : 'shift-right' });
    out.push({ kind: 'hard-drop' });
    return out;
  }

  // ---- grid simulation ----

  private snapshotGrid(): Cell[] {
    return this.engine.board.grid.slice();
  }

  private outOfBounds(piece: ActivePiece): boolean {
    const s = shapeOf(piece.type, piece.rotation);
    for (const [dx] of s) {
      const x = piece.x + dx;
      if (x < 0 || x >= BOARD_WIDTH) return true;
    }
    return false;
  }

  /** Returns a piece dropped as far as it can go, or null if doesn't fit. */
  private dropPiece(grid: Cell[], piece: ActivePiece): ActivePiece | null {
    const s = shapeOf(piece.type, piece.rotation);
    // Start from top of buffer; drop until first contact.
    let y = 0;
    while (!this.collides(grid, s, piece.x, y + 1) && y < 39) y++;
    if (this.collides(grid, s, piece.x, y)) return null;
    return { ...piece, y };
  }

  private collides(grid: Cell[], shape: ReadonlyArray<readonly [number, number]>, x: number, y: number): boolean {
    for (const [dx, dy] of shape) {
      const px = x + dx;
      const py = y + dy;
      if (px < 0 || px >= BOARD_WIDTH || py >= BOARD_BUFFER_HEIGHT + BOARD_VISIBLE_HEIGHT) return true;
      if (py < 0) continue;
      if (grid[py * BOARD_WIDTH + px] !== null) return true;
    }
    return false;
  }

  private applyPlace(grid: Cell[], piece: ActivePiece): { x: number; y: number }[] {
    const cells: { x: number; y: number }[] = [];
    const s = shapeOf(piece.type, piece.rotation);
    for (const [dx, dy] of s) {
      const px = piece.x + dx;
      const py = piece.y + dy;
      if (py < 0 || py >= BOARD_BUFFER_HEIGHT + BOARD_VISIBLE_HEIGHT) continue;
      const idx = py * BOARD_WIDTH + px;
      grid[idx] = piece.type;
      cells.push({ x: px, y: py });
    }
    return cells;
  }

  /** Clear full rows in-place, returning the rows cleared. */
  private countAndClearFull(grid: Cell[]): number[] {
    const cleared: number[] = [];
    for (let y = 0; y < BOARD_BUFFER_HEIGHT + BOARD_VISIBLE_HEIGHT; y++) {
      let full = true;
      for (let x = 0; x < BOARD_WIDTH; x++) {
        if (grid[y * BOARD_WIDTH + x] === null) { full = false; break; }
      }
      if (full) cleared.push(y);
    }
    for (const r of cleared) {
      for (let y = r; y > 0; y--) {
        for (let x = 0; x < BOARD_WIDTH; x++) {
          grid[y * BOARD_WIDTH + x] = grid[(y - 1) * BOARD_WIDTH + x]!;
        }
      }
      for (let x = 0; x < BOARD_WIDTH; x++) grid[x] = null;
    }
    return cleared;
  }

  private undoPlace(grid: Cell[], cells: { x: number; y: number }[], cleared: number[]): void {
    // Easiest: re-snapshot the engine grid (cheap enough at 10x40 = 400 cells).
    const fresh = this.engine.board.grid;
    for (let i = 0; i < grid.length; i++) grid[i] = fresh[i]!;
    void cells; void cleared;
  }

  private evaluate(grid: Cell[], cleared: number[]): number {
    const w = this.difficulty.weights;
    const colHeights = new Array<number>(BOARD_WIDTH).fill(0);
    for (let x = 0; x < BOARD_WIDTH; x++) {
      for (let y = 0; y < BOARD_BUFFER_HEIGHT + BOARD_VISIBLE_HEIGHT; y++) {
        if (grid[y * BOARD_WIDTH + x] !== null) {
          colHeights[x] = (BOARD_BUFFER_HEIGHT + BOARD_VISIBLE_HEIGHT) - y;
          break;
        }
      }
    }
    let aggregate = 0; for (const h of colHeights) aggregate += h;
    let holes = 0;
    for (let x = 0; x < BOARD_WIDTH; x++) {
      const top = (BOARD_BUFFER_HEIGHT + BOARD_VISIBLE_HEIGHT) - colHeights[x]!;
      for (let y = top + 1; y < BOARD_BUFFER_HEIGHT + BOARD_VISIBLE_HEIGHT; y++) {
        if (grid[y * BOARD_WIDTH + x] === null) holes++;
      }
    }
    let bumpiness = 0;
    for (let x = 0; x < BOARD_WIDTH - 1; x++) bumpiness += Math.abs(colHeights[x]! - colHeights[x + 1]!);
    // Right-most column kept low to leave room for I-piece tetrises.
    const rightHeight = colHeights[BOARD_WIDTH - 1]!;
    const otherHeightAvg = aggregate / BOARD_WIDTH;
    const rightWellBonus = Math.max(0, otherHeightAvg - rightHeight);
    // Wells: empty cells with both neighbors filled
    let wells = 0;
    for (let x = 0; x < BOARD_WIDTH; x++) {
      const h = colHeights[x]!;
      const lh = x > 0 ? colHeights[x - 1]! : 100;
      const rh = x < BOARD_WIDTH - 1 ? colHeights[x + 1]! : 100;
      const depth = Math.min(lh, rh) - h;
      if (depth > 0) wells += depth;
    }
    return (
      w.aggregateHeight * aggregate +
      w.lines * cleared.length +
      w.holes * holes +
      w.bumpiness * bumpiness +
      w.wells * wells +
      w.rightWell * rightWellBonus
    );
  }
}

/** Convert an AI PendingAction into the Engine input it represents. */
export function applyBotAction(engine: Engine, action: PendingAction): void {
  if (engine.state !== 'playing') return;
  switch (action.kind) {
    case 'rotate-cw': engine.rotate('cw'); break;
    case 'rotate-ccw': engine.rotate('ccw'); break;
    case 'shift-left':
      engine.setInput({ left: true }, performance.now());
      engine.setInput({ left: false }, performance.now());
      break;
    case 'shift-right':
      engine.setInput({ right: true }, performance.now());
      engine.setInput({ right: false }, performance.now());
      break;
    case 'hard-drop': engine.hardDrop(); break;
    case 'hold': engine.hold(); break;
  }
}
